import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, ExternalLink, Mail } from "lucide-react";

const SUPPORT_EMAIL = "support@seospider.app";

const Help = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailto = `mailto:${SUPPORT_EMAIL}?subject=SEO%20Spider%20Support%20from%20${encodeURIComponent(form.name)}&body=${encodeURIComponent(form.message + "\n\nFrom: " + form.email)}`;
    window.location.href = mailto;
  };

  const cardBase = {
    background: "#fff",
    borderRadius: "16px",
    padding: "1.5rem",
    height: "100%",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    transition: "all 0.3s ease",
  };

  return (
    <div
      className="py-4"
      style={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        borderRadius: 16,
      }}
    >
      <div className="container-fluid px-4">
        {/* Header */}
        <div className="text-center mb-4">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 64,
              height: 64,
              background: "linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary-dark) 100%)",
              borderRadius: 16,
              boxShadow: "0 4px 12px rgba(12, 44, 85, 0.25)",
            }}
          >
            <HelpCircle size={32} style={{ color: "#fff" }} />
          </div>
          <h4
            className="fw-bold mb-2"
            style={{
              fontSize: "1.5rem",
              color: "#0f172a",
              letterSpacing: "-0.02em",
            }}
          >
            {t("help.title")}
          </h4>
          <p
            className="text-muted mb-0"
            style={{ fontSize: "0.95rem", maxWidth: 600, margin: "0 auto" }}
          >
            {t("help.subtitle")}
          </p>
        </div>

        {/* Cards Grid: Documentation + Support */}
        <div className="row g-4 mb-4">
          {/* Documentation Card */}
          <div className="col-md-6">
            <div
              style={{
                ...cardBase,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.borderColor = "var(--app-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
              onClick={() =>
                window.open("https://seospider.app/documentation?page=ecwid", "_blank")
              }
            >
              <div className="d-flex align-items-start gap-3">
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "var(--app-primary-lighter)",
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ExternalLink size={24} style={{ color: "var(--app-primary)" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h5
                    className="fw-bold mb-2"
                    style={{ fontSize: "1.125rem", color: "#0f172a" }}
                  >
                    {t("help.documentation")}
                  </h5>
                  <p
                    className="mb-3"
                    style={{
                      fontSize: "0.9rem",
                      color: "#64748b",
                      lineHeight: 1.6,
                    }}
                  >
                    {t("help.docsDescLong")}
                  </p>
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{
                      color: "var(--app-primary)",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    <span>{t("help.viewDocumentation")}</span>
                    <ExternalLink size={14} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Support Card */}
          <div className="col-md-6">
            <div
              style={cardBase}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.08)";
                e.currentTarget.style.borderColor = "#10b981";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
            >
              <div className="d-flex align-items-start gap-3">
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Mail size={24} style={{ color: "#10b981" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h5
                    className="fw-bold mb-2"
                    style={{ fontSize: "1.125rem", color: "#0f172a" }}
                  >
                    {t("help.support")}
                  </h5>
                  <p
                    className="mb-3"
                    style={{
                      fontSize: "0.9rem",
                      color: "#64748b",
                      lineHeight: 1.6,
                    }}
                  >
                    {t("help.supportDescLong")}
                  </p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-decoration-none d-inline-flex align-items-center gap-2"
                    style={{
                      color: "#10b981",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#059669";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#10b981";
                    }}
                  >
                    <span>{SUPPORT_EMAIL}</span>
                    <Mail size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback & feature requests */}
        <div
          className="mb-4"
          style={{
            background: "linear-gradient(135deg, rgba(255, 243, 205, 0.4) 0%, rgba(255, 237, 213, 0.3) 100%)",
            borderRadius: 16,
            padding: "1.25rem 1.5rem",
            border: "1px solid rgba(253, 126, 20, 0.2)",
          }}
        >
          <h6
            className="fw-semibold mb-2"
            style={{ fontSize: "1rem", color: "#0f172a" }}
          >
            {t("help.feedbackTitle")}
          </h6>
          <p className="mb-0" style={{ fontSize: "0.9rem", color: "#475569", lineHeight: 1.6 }}>
            {t("help.feedbackMessage")}{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=SEO%20Spider%20Feedback%20%2F%20Feature%20Request`}
              className="fw-semibold text-decoration-none"
              style={{ color: "var(--app-primary)" }}
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>

        {/* Quick Tips */}
        <div
          style={{
            background: "var(--app-primary-lighter)",
            borderRadius: 16,
            padding: "1.5rem",
            border: "1px solid var(--app-primary-light)",
          }}
        >
          <h6
            className="fw-semibold mb-3"
            style={{ fontSize: "1rem", color: "#0f172a" }}
          >
            Quick Tips
          </h6>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="d-flex align-items-start gap-2">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--app-primary)",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: "#475569",
                    lineHeight: 1.6,
                  }}
                >
                  Start with the Dashboard to see your SEO health overview
                </span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-start gap-2">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--app-primary)",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: "#475569",
                    lineHeight: 1.6,
                  }}
                >
                  Use SEO Optimizer → Bulk optimize to update multiple products at once
                </span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-start gap-2">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--app-primary)",
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: "#475569",
                    lineHeight: 1.6,
                  }}
                >
                  Enable auto-validate after bulk update to keep SEO cache up to date
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
